'use client';

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tx,
  asList,
  courseDetailHref,
  COURSE_DETAIL_BASE,
  type LearningItem,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { sectionInner, sectionTitle, cardReset } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { HeadingLine } from '../_shared/HeadingLine';
import { BlockRoot } from '../_shared/BlockRoot';
import { PODCAST_PLUGIN_ID, usePodcastCourses, type PodcastSource } from './podcastCatalog';

/**
 * Podcast block with two views:
 *
 * <ul>
 *   <li>{@code grid} (default) — equal cards, each with a coloured
 *       cover tile, play disc, episode pill, description, footer CTA.</li>
 *   <li>{@code featured} — the first item becomes a big hero card
 *       (cover art, badge, episode headline, listen row) with the
 *       remaining items stacked as compact episode rows beside it.</li>
 * </ul>
 *
 * <p>Two data modes, same as the other catalog-backed home blocks:
 * <ul>
 *   <li><b>Live</b> ({@code source: 'catalog'}, the default) — pulls
 *       podcast courses from
 *       {@code LearningSource.searchCourses({ filter: { pluginIds } })}.
 *       {@code pluginIds} is the backend's course-type discriminator, so
 *       one filter — {@code ['C8Podcast']} — is the whole query.</li>
 *   <li><b>Authored</b> ({@code source: 'authored'}) — renders the
 *       {@code items} array as-is, no fetch.</li>
 * </ul>
 * <p><b>No content, no block.</b> The section renders only when it has
 * real cards to show: in live mode that means the catalog answered with
 * podcasts — the authored {@code items} are never used as a stand-in, and
 * nothing is drawn while the fetch is in flight, so a tenant without
 * podcast courses never sees placeholder cards or an empty shell.
 *
 * Everything the catalog can't supply stays prop-driven.
 *
 * <p>Author fields per item: {@code title, episodes, color}
 * (any CSS colour or var(--…) ref), {@code description, href}. The
 * featured view additionally reads {@code image, badge, episodeLabel,
 * episodeTitle, duration} and falls back to the coloured cover tile
 * whenever {@code image} is empty.
 */
/** Admin-authored text: a plain string, or a per-locale bundle the
 *  editor writes for {@code x-localized} fields. {@link tx} resolves
 *  either shape at render. */
type Localized = string | Record<string, string>;

interface Podcast {
  title: Localized;
  episodes?: Localized;
  color?: string;
  description?: Localized;
  href?: string;
  /** Cover art URL. Featured view only — empty falls back to the
   *  coloured gradient tile the grid view uses. */
  image?: string;
  /** Small accent label above the hero title, e.g. "ÖNE ÇIKAN". */
  badge?: Localized;
  /** Episode ordinal line, e.g. "01. Bölüm". */
  episodeLabel?: Localized;
  /** Episode headline. Falls back to {@code title} on the rows. */
  episodeTitle?: Localized;
  /** Runtime, e.g. "45:12". Not localized — a time code reads the same
   *  in every language. */
  duration?: string;
}

/** Which view the block renders. {@code grid} is the original layout. */
export type PodcastsVariant = 'grid' | 'featured';

/** Where the cards come from — the live catalog or the authored array. */
export type PodcastsSource = PodcastSource;

export interface PodcastsProps {
  title?: Localized;
  eyebrow?: Localized;
  footerLabel?: Localized;
  /** {@code grid} (default) = equal cards; {@code featured} = one hero
   *  card + a stack of episode rows beside it. */
  variant?: PodcastsVariant;
  /** Label on the hero's play button in the featured view. */
  playLabel?: Localized;
  /** {@code catalog} (default) pulls podcast courses from the LMS;
   *  {@code authored} renders {@code items} only, no fetch. */
  source?: PodcastsSource;
  /** Course-plugin id the catalog is filtered by. The backend tags each
   *  course with its plugin, so this single filter IS the podcast query.
   *  Only change it to target another plugin family. */
  pluginId?: string;
  /** How many podcasts to pull in live mode. */
  limit?: number;
  /** Catalog sort for the live feed — the {@code searchCourses}
   *  {@code sortField} vocabulary ({@code recent} | {@code popular} |
   *  {@code published} | {@code viewed}). */
  sortField?: string;
  /** Base path for a live card's link. Defaults to the canonical
   *  course-detail namespace ({@code /lxp/course}). */
  hrefBase?: string;
  /** Authored cards. In live mode they render as the fallback (while
   *  loading, and when the catalog has no podcasts). */
  items?: Podcast[];
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading.
   *  On by default — this block shipped with the rule. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

type PodcastCardVars = CSSProperties & { '--pod-accent': string };

/** Cover tints cycled across live cards so a catalog-driven grid keeps
 *  the authored look (every card would otherwise share one accent). */
const LIVE_ACCENTS = [
  'var(--color-brand-500, #3b82f6)',
  'var(--color-accent-500, #8b5cf6)',
  'var(--color-amber-500, #f59e0b)',
];

/** Catalog course → card. The catalog has no episode model, so the
 *  episode fields carry what a podcast course does have: runtime in the
 *  pill, the trainer on the label line. */
function itemToPodcast(item: LearningItem, index: number, hrefBase: string): Podcast {
  return {
    title: item.title,
    episodes: item.duration,
    color: LIVE_ACCENTS[index % LIVE_ACCENTS.length],
    description: item.description,
    href: item.href ?? courseDetailHref(item.title, item.id, hrefBase),
    image: item.imageUrl,
    episodeLabel: item.instructor,
    duration: item.duration,
  };
}

export function Podcasts({
  title,
  eyebrow,
  footerLabel,
  variant = 'grid',
  playLabel,
  source = 'catalog',
  pluginId = PODCAST_PLUGIN_ID,
  limit = 3,
  sortField = 'recent',
  hrefBase = COURSE_DETAIL_BASE,
  items,
  style,
  paddingOuter,
  headingLine = true,
  paddingInner,
}: PodcastsProps): ReactElement | null {
  const { t } = useTranslation('site-builder-blocks');
  const authored = asList<Podcast>(items);
  const courses = usePodcastCourses({
    enabled: source !== 'authored',
    pluginId,
    limit,
    sortField,
  });

  // Live mode shows the catalog and nothing else: the authored array is
  // an editing convenience, never a stand-in for a tenant that has no
  // podcast courses. Anything else would put demo cards in front of
  // users as if they were real content.
  // Keyed off `source`: a catalog block on a host with no LearningSource
  // (admin preview) shows nothing either, instead of falling back to
  // whatever demo rows the seed left in `items`.
  const list =
    source === 'authored' ? authored : courses.map((item, i) => itemToPodcast(item, i, hrefBase));

  // Nothing real to show (still fetching, catalog empty, or no authored
  // cards) — drop the whole section rather than draw a bare heading or a
  // skeleton that resolves to nothing.
  if (list.length === 0) return null;

  // Localized bundles are objects — resolve before any truthiness guard.
  const eyebrowText = tx(eyebrow ?? t('podcasts.eyebrow'));
  const isFeatured = variant === 'featured';
  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-16, 64px) var(--space-6, 24px)',
        ...resolvePaddingStyle(paddingOuter),
        ...style,
      }}
      aria-labelledby="podcasts-title"
    >
      <div style={{ ...sectionInner, ...resolvePaddingStyle(paddingInner) }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 20,
          }}
        >
          <div>
            {eyebrowText && (
              <div
                style={{
                  marginBottom: 8,
                  fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-secondary, #71717a)',
                }}
              >
                {eyebrowText}
              </div>
            )}
            <h2 id="podcasts-title" style={sectionTitle}>
              {tx(title ?? t('podcasts.title'))}
            </h2>
          </div>
          {headingLine && <HeadingLine />}
        </header>

        {isFeatured ? (
          <FeaturedView list={list} playLabel={tx(playLabel ?? t('podcasts.playLabel'))} />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
              gap: 'clamp(16px, 2.4vw, 24px)',
              marginTop: 'clamp(22px, 4vw, 36px)',
            }}
          >
            {list.map((p, i) => (
              <PodcastCard
                key={`${tx(p.title)}-${i}`}
                podcast={p}
                index={i}
                footerLabel={tx(footerLabel ?? t('podcasts.footerLabel'))}
              />
            ))}
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

// ---------------------------------------------------------------------------
// Featured view — one hero card + a stack of episode rows beside it
// ---------------------------------------------------------------------------

// Static (unscoped) CSS: every value is fixed, so multiple instances of the
// block on one page collapse to the same rules — same trick CourseRow's
// container grid uses. Media queries are the reason this can't be inline.
const FEATURED_CSS = `
.sb-pod-feat {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: clamp(12px, 1.6vw, 18px);
  margin-top: clamp(22px, 4vw, 36px);
  align-items: stretch;
}
.sb-pod-feat--solo { grid-template-columns: minmax(0, 1fr); }
.sb-pod-rows {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 1.6vw, 18px);
  min-width: 0;
}
/* Rows share the hero's height evenly, so two rows line up with one hero. */
.sb-pod-row { flex: 1 1 0; min-height: 92px; }
@media (max-width: 900px) {
  .sb-pod-feat { grid-template-columns: minmax(0, 1fr); }
  .sb-pod-row { flex: 0 0 auto; }
}
@media (max-width: 520px) {
  .sb-pod-hero { flex-direction: column; }
  .sb-pod-hero-cover { width: 100% !important; }
}
`;

function FeaturedView({ list, playLabel }: { list: Podcast[]; playLabel: string }) {
  const [hero, ...rows] = list;
  if (!hero) return null;
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FEATURED_CSS }} />
      <div className={`sb-pod-feat${rows.length === 0 ? ' sb-pod-feat--solo' : ''}`}>
        <FeaturedHero podcast={hero} playLabel={playLabel} />
        {rows.length > 0 && (
          <div className="sb-pod-rows">
            {rows.map((p, i) => (
              <EpisodeRow key={`${tx(p.title)}-${i}`} podcast={p} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** Shared shell for both featured cards: a link when the item has an
 *  href, an article otherwise — same contract as {@link PodcastCard}. */
function CardShell({
  href,
  ariaLabel,
  className,
  style,
  children,
}: {
  href?: string;
  ariaLabel: string;
  className?: string;
  style: CSSProperties;
  children: ReactNode;
}) {
  const surface: CSSProperties = {
    ...cardReset,
    background: 'var(--color-surface-card, #fff)',
    borderRadius: 'var(--radius-xl, 24px)',
    color: 'var(--color-text-primary, #18181b)',
    textDecoration: 'none',
    overflow: 'hidden',
    ...style,
  };
  if (href) {
    return (
      <Card asChild>
        <a href={href} className={className} style={surface} aria-label={ariaLabel}>
          {children}
        </a>
      </Card>
    );
  }
  return (
    <Card asChild>
      <article className={className} style={surface}>
        {children}
      </article>
    </Card>
  );
}

function FeaturedHero({ podcast, playLabel }: { podcast: Podcast; playLabel: string }) {
  const accent = podcast.color || 'var(--color-brand-500, #3b5bdb)';
  const titleText = tx(podcast.title);
  const episodeHeadline = tx(podcast.episodeTitle) || tx(podcast.description);
  const label = tx(playLabel);
  // Resolve before the render guards: a localized bundle is an object,
  // so it is always truthy even when it holds nothing for this locale.
  const badgeText = tx(podcast.badge);
  const episodeLabelText = tx(podcast.episodeLabel);

  return (
    <CardShell
      href={podcast.href}
      ariaLabel={titleText}
      className="sb-pod-hero"
      style={{
        display: 'flex',
        gap: 'clamp(16px, 2.2vw, 24px)',
        padding: 'clamp(16px, 2.2vw, 24px)',
      }}
    >
      <Cover
        className="sb-pod-hero-cover"
        image={podcast.image}
        accent={accent}
        size="clamp(120px, 15vw, 180px)"
        radius="var(--radius-lg, 16px)"
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {badgeText && (
          <span
            style={{
              alignSelf: 'flex-start',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-widest, 0.12em)',
              textTransform: 'uppercase',
              color: accent,
            }}
          >
            {badgeText}
          </span>
        )}
        <h3
          style={{
            margin: 0,
            fontSize: 'clamp(18px, 2.2vw, 24px)',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {titleText}
        </h3>
        {episodeLabelText && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {episodeLabelText}
          </span>
        )}
        {episodeHeadline && (
          <p
            style={{
              margin: 0,
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              lineHeight: 1.45,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {episodeHeadline}
          </p>
        )}
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <PlayDisc size={36} />
          {label && (
            <span style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600 }}>{label}</span>
          )}
          {podcast.duration && (
            <span
              style={{
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--color-text-tertiary, #94867d)',
              }}
            >
              {tx(podcast.duration)}
            </span>
          )}
        </div>
      </div>
    </CardShell>
  );
}

function EpisodeRow({ podcast }: { podcast: Podcast }) {
  const accent = podcast.color || 'var(--color-brand-500, #3b5bdb)';
  // Rows lead with the episode headline; the series title is the fallback
  // so a partially-authored item still reads sensibly.
  const headline = tx(podcast.episodeTitle) || tx(podcast.title);
  const label = tx(podcast.episodeLabel) || tx(podcast.episodes);

  return (
    <CardShell
      href={podcast.href}
      ariaLabel={headline}
      className="sb-pod-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(12px, 1.6vw, 16px)',
        padding: 'clamp(12px, 1.6vw, 16px)',
      }}
    >
      <Cover
        image={podcast.image}
        accent={accent}
        size="clamp(56px, 7vw, 76px)"
        radius="var(--radius-md, 12px)"
        compact
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {label && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {label}
          </span>
        )}
        <div
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 600,
            lineHeight: 1.35,
          }}
        >
          {headline}
        </div>
      </div>
      {podcast.duration && (
        <span
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            color: 'var(--color-text-tertiary, #94867d)',
            flexShrink: 0,
          }}
        >
          {tx(podcast.duration)}
        </span>
      )}
      <span style={{ marginLeft: 2, flexShrink: 0 }}>
        <PlayDisc size={32} />
      </span>
    </CardShell>
  );
}

/** Cover art: the authored image when there is one, otherwise the same
 *  coloured gradient tile the grid view draws. */
function Cover({
  image,
  accent,
  size,
  radius,
  compact = false,
  className,
}: {
  image?: string;
  accent: string;
  size: string;
  radius: string;
  compact?: boolean;
  className?: string;
}) {
  const base: CSSProperties = {
    width: size,
    flexShrink: 0,
    aspectRatio: '1 / 1',
    borderRadius: radius,
    overflow: 'hidden',
  };

  if (image) {
    return (
      <div className={className} style={base}>
        <img
          src={image}
          alt=""
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div
      aria-hidden
      className={className}
      style={{
        ...base,
        background: `radial-gradient(circle at 30% 30%, color-mix(in srgb, ${accent} 70%, #fff), ${accent})`,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        style={{
          width: compact ? '46%' : '40%',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.4)',
          border: '2px solid rgba(255,255,255,0.3)',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--color-text-on-brand, #fff)',
        }}
      >
        <PlayGlyph size={compact ? 14 : 20} />
      </div>
    </div>
  );
}

/** Filled play button. Inverted pair (`text-primary` fill on a
 *  `surface-card` glyph) so it stays legible in both light and dark
 *  themes — a hardcoded white disc would vanish on a light surface. */
function PlayDisc({ size }: { size: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        background: 'var(--color-text-primary, #18181b)',
        color: 'var(--color-surface-card, #fff)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <PlayGlyph size={Math.round(size * 0.45)} />
    </span>
  );
}

function PlayGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M8 5.8v12.4c0 .8.9 1.3 1.6.9l9.4-6.2c.6-.4.6-1.4 0-1.8L9.6 4.9C8.9 4.5 8 5 8 5.8z" />
    </svg>
  );
}

function PodcastCard({
  podcast,
  index,
  footerLabel,
}: {
  podcast: Podcast;
  index: number;
  footerLabel: string;
}) {
  const accent = podcast.color || 'var(--color-brand-500, #3b5bdb)';
  const style: PodcastCardVars = {
    ...cardReset,
    '--pod-accent': accent,
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    gap: 'clamp(14px, 2vw, 18px)',
    minHeight: 168,
    padding: 'clamp(14px, 2.2vw, 18px)',
    borderRadius: 'var(--radius-xl, 24px)',
    background: `linear-gradient(145deg, color-mix(in srgb, var(--color-surface-card, #fff) 92%, ${accent} 8%), var(--color-surface-card, #fff))`,
    color: 'var(--color-text-primary, #18181b)',
    textDecoration: 'none',
    overflow: 'hidden',
  };
  const titleText = tx(podcast.title);
  const episodeText = tx(podcast.episodes);
  const descriptionText = tx(podcast.description);

  const inner = (
    <>
      <div
        aria-hidden
        style={{
          width: 120,
          flexShrink: 0,
          aspectRatio: '1 / 1',
          borderRadius: 'var(--radius-lg, 16px)',
          background: `radial-gradient(circle at 30% 30%, color-mix(in srgb, ${accent} 70%, #fff), ${accent})`,
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.4)',
            border: '2px solid rgba(255,255,255,0.3)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--color-text-on-brand, #fff)',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5.8v12.4c0 .8.9 1.3 1.6.9l9.4-6.2c.6-.4.6-1.4 0-1.8L9.6 4.9C8.9 4.5 8 5 8 5.8z" />
          </svg>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {episodeText && (
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                textTransform: 'uppercase',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {episodeText}
            </span>
          )}
          <span
            style={{
              fontSize: '11px',
              fontFamily: 'var(--font-family-mono, monospace)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md, 16px)',
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {titleText}
        </h3>
        {descriptionText && (
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              lineHeight: 1.55,
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            {descriptionText}
          </p>
        )}
        {footerLabel && (
          <div
            aria-hidden
            style={{
              marginTop: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 600,
              color: accent,
            }}
          >
            <span>{footerLabel}</span>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </div>
        )}
      </div>
    </>
  );

  if (podcast.href) {
    return (
      <Card asChild>
        <a
          href={podcast.href}
          style={style}
          aria-label={`${titleText}${episodeText ? `, ${episodeText}` : ''}`}
        >
          {inner}
        </a>
      </Card>
    );
  }
  return (
    <Card asChild>
      <article style={style}>{inner}</article>
    </Card>
  );
}
